/**
 * @fileoverview Os controles de escolha dos modais da cobrança (31/10/2026,
 * etapas 7 a 9 dos planos), tirados do modal da contratação para a redução usar
 * os mesmos: o grupo de opções de escolha única (uma parada no Tab, as setas
 * andam), o número entre o menos e o mais (dá para digitar: com 30 pessoas, ninguém
 * clica 28 vezes), o texto de um erro do servidor e a linha de cada item no resumo.
 */

import React, { useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";

import { InfoTip } from "../../../pages/Flows/components/TriageManager/InfoTip";
import { i18n } from "../../../translate/i18n";

const t = (key, options) => i18n.t(`plansDialog.${key}`, options);

/** O motivo de um erro do servidor, traduzido (o que os modais mostram no resumo). */
export const billingErrorText = (code) => (code && i18n.exists(`backendErrors.${code}`) ? i18n.t(`backendErrors.${code}`) : t("quoteFailed"));

/** Uma linha do resumo do mês: o plano × licenças, o Chat Interno × licenças dele, o pacote de IA. */
export const contractItemLabel = (item) => {
  if (item.code === "plan") return t("items.plan", { plan: item.name, count: item.quantity });
  if (item.code === "internal_chat") return t("items.internal", { count: item.quantity });
  return t("items.pack", { messages: Number(item.messages || 0).toLocaleString("pt-BR") });
};

/**
 * Um grupo de opções de escolha única: uma parada só no Tab (a opção marcada) e
 * as setas andam entre as que estão disponíveis.
 */
export const RadioGroup = ({ className, label, labelledBy, options, value, onChange, renderOption }) => {
  const refs = useRef([]);
  const enabled = options.filter((option) => !option.disabled);
  const move = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const index = enabled.findIndex((option) => option.value === value);
    const next = enabled[(index + keys[event.key] + enabled.length) % enabled.length];
    onChange(next.value);
    const node = refs.current[options.indexOf(next)];
    if (node) node.focus();
  };
  return (
    <div className={className} role="radiogroup" aria-label={label} aria-labelledby={labelledBy} onKeyDown={move}>
      {options.map((option, index) => (
        <React.Fragment key={option.value}>
          {renderOption(option, {
            ref: (node) => {
              refs.current[index] = node;
            },
            role: "radio",
            "aria-checked": option.value === value,
            "aria-disabled": option.disabled || undefined,
            tabIndex: option.value === value ? 0 : -1,
            onClick: () => !option.disabled && onChange(option.value),
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

// Só para leitores de tela: o número mudou pelo menos ou pelo mais.
const SR_ONLY = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };

/**
 * O número entre o menos e o mais. Digitado, subir vale na hora; descer, só ao sair
 * do campo (ou no Enter): o "3" de "35" não pode virar 3 licenças no meio do caminho,
 * nem cortar o que depende delas. Ao sair, o que passou volta para o limite.
 */
export const NumberStepper = ({ classes, name, value, min, max, onChange }) => {
  const [draft, setDraft] = useState(null);
  // aria-disabled (e não disabled): o botão no limite continua com o foco, em vez de devolvê-lo ao topo do modal.
  const step = (delta) => {
    const next = value + delta;
    if (next >= min && next <= max) onChange(next);
  };
  const type = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    setDraft(digits);
    const next = Number(digits);
    if (digits && next > value && next <= max) onChange(next);
  };
  const settle = () => {
    if (draft === null) return;
    if (draft) onChange(Math.min(Math.max(Number(draft), min), max));
    setDraft(null);
  };
  const keys = (event) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      setDraft(null);
      step(event.key === "ArrowUp" ? 1 : -1);
    } else if (event.key === "Enter") {
      settle();
    }
  };
  return (
    <div className={classes.stepper} role="group" aria-label={name}>
      <button type="button" className={classes.stepButton} onClick={() => step(-1)} aria-disabled={value <= min} aria-label={`${t("decrease")} ${name}`}>
        <Minus />
      </button>
      <input
        className={classes.stepValue}
        value={draft === null ? value : draft}
        onChange={type}
        onBlur={settle}
        onKeyDown={keys}
        onFocus={(event) => event.target.select()}
        inputMode="numeric"
        autoComplete="off"
        role="spinbutton"
        aria-label={name}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
      />
      <span style={SR_ONLY} aria-live="polite">
        {value}
      </span>
      <button type="button" className={classes.stepButton} onClick={() => step(1)} aria-disabled={value >= max} aria-label={`${t("increase")} ${name}`}>
        <Plus />
      </button>
    </div>
  );
};

export const Stepper = ({ classes, name, hint, help, value, min, max, onChange }) => (
  <div className={classes.stepperRow}>
    <div className={classes.stepperLabel}>
      <span className={classes.stepperName}>
        {name}
        {help && <InfoTip text={help} />}
      </span>
      {hint && <span className={classes.stepperHint}>{hint}</span>}
    </div>
    <NumberStepper classes={classes} name={name} value={value} min={min} max={max} onChange={onChange} />
  </div>
);
